import React, { useEffect } from 'react';
import './style.css';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    fetchCustomerDataById,
    fetchCustomerAuditTrail,
    resetCustomer,
    setSearchTerm, selectCustomer,
    selectBookings,
    selectAuditTrail, setBookingSearchId, setBookingSearchUserInput, setSearchType,
    fetchCustomerTrips
} from '../../redux/slices/customer-slice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { Tabs } from '@vwfs-bronson/bronson-react'
import PersonalDataView from "../../components/details-view/personal-data";
import PaymentMethodDataView from "../../components/details-view/payment-method-data";
import IdVerificationDataView from "../../components/details-view/id-verification-data";
import BookingHistoryDataView from "../../components/details-view/booking-history";
import TripHistoryDataView from "../../components/details-view/trip-history";
import AuditTrailDataView from "../../components/details-view/audit-trail-data";
import AuditTrailDataFilter from "../../components/details-view/audit-trail-filter";
import { TabIdMap, useSelectedTab } from '../../hooks/useSelectedTab';
import TripHistoryDataFilter from '../../components/details-view/trip-history-filter';
import AgreementsDataView from '../../components/details-view/agreements-data';
import { fetchBookingDataBySearch, fetchCustomerBookings } from "../../redux/slices/customer-bookings";
import BookingHistoryDataFilter from '../../components/details-view/booking-history-filter';
import { BackNavButton, RefreshNavButton } from '../../components/base/navigation-button';
import AppWrapper from '../../components/base/app-wrapper';
import { resetErrorDialog, selectNotificationStore } from '../../redux/slices/notification-slice';
import { selectTrips } from '../../redux/slices/customer-trips';
import { resetPaymentsList } from '../../redux/slices/payments-slice';
import CommentsDataView from "../../components/details-view/comments-data";


export default function DetailViewPage() {
    const { customerPathId } = useParams();

    const customer = useAppSelector(selectCustomer());
    const notification = useAppSelector(selectNotificationStore());
    const bookings = useAppSelector(selectBookings());
    const trips = useAppSelector(selectTrips());
    const auditTrails = useAppSelector(selectAuditTrail());

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const isGuestMode = customer.id === 'guest';
    const [searchParams] = useSearchParams();

    const navigateToStartpage = () => {
        dispatch(setSearchTerm(undefined));
        dispatch(resetErrorDialog());
        dispatch(resetCustomer());
        dispatch(resetPaymentsList());
        navigate('/');
    };

    const { selectedTabId, onTabClicked } = useSelectedTab();

    useEffect(() => {
        if (customerPathId && customerPathId != customer.customer?.id) {
            //dispatch(fetchCustomerDataById({ id: customerPathId }));
            fetchTabData(true);
        }
    }, [customerPathId]);

    useEffect(() => {
        if (!selectedTabId) {
            return;
        }

        fetchTabData();
    }, [selectedTabId])

    const reloadPage = () => {
        if (!customerPathId) {
            return;
        }
        fetchTabData(true)
    }

    const fetchTabData = (reload?: boolean) => {
        dispatch(setSearchTerm(undefined));
        dispatch(resetErrorDialog());
        dispatch(resetErrorDialog());

        if (!customer || reload) {
            dispatch(resetCustomer());
            if (customerPathId !== 'guest') {
                dispatch(fetchCustomerDataById({ id: customerPathId }));
            }
        }

        if (selectedTabId === TabIdMap.BookingHistory) {
            if (!bookings?.data?.length || reload) {
                if (customerPathId !== 'guest') {
                    dispatch(fetchCustomerBookings({ id: customerPathId }));
                } else {
                    dispatch(setBookingSearchId(searchParams.get('bookingId')));
                    dispatch(setBookingSearchUserInput(searchParams.get('userInput')));
                    dispatch(setSearchType({ type: 'BOOKING' }));
                    dispatch(fetchBookingDataBySearch(true));
                }
            }
        } else if (selectedTabId === TabIdMap.AuditTrail) {
            if (!auditTrails?.data?.length || reload) dispatch(fetchCustomerAuditTrail({ id: customerPathId }));
        } else if (selectedTabId === TabIdMap.TripHistory) {
            if (!trips?.data?.length || reload) dispatch(fetchCustomerTrips({ id: customerPathId }));
        }
    }

    const handleOnChangeSelected = (selectedTab) => {
        onTabClicked(selectedTab);
    }

    const isLoading =
        customer?.status == 'loading' ||
        bookings?.status == 'loading' ||
        trips?.status === 'loading' ||
        auditTrails?.status === 'loading';

    return (
        <AppWrapper
            customer={customer}
            leftNavActions={
                <>
                    <BackNavButton onClickAction={navigateToStartpage} />
                    <RefreshNavButton onClickAction={reloadPage} />
                </>
            }
            loading={isLoading}
            errorDialog={notification?.errorDialog}
        >
            <div className="detail-container">
                <Tabs selected={selectedTabId} onChangeSelected={handleOnChangeSelected}>
                    <Tabs.Header testId='tabsHeaders'>
                        <Tabs.HeaderItem tabId={TabIdMap.PersonalData} icon="user" className="details-tab" disabled={isGuestMode}>
                            &nbsp;&nbsp;Personal Data
                        </Tabs.HeaderItem>
                        <Tabs.HeaderItem tabId={TabIdMap.IdVerificationData} icon="account-detail" className="details-tab" disabled={isGuestMode}>
                            &nbsp;&nbsp;ID Verification Data
                        </Tabs.HeaderItem>
                        <Tabs.HeaderItem tabId={TabIdMap.PaymentData} icon="coins-circle-eurosign" className="details-tab" disabled={isGuestMode}>
                            &nbsp;&nbsp;Payment Data
                        </Tabs.HeaderItem>
                        <Tabs.HeaderItem tabId={TabIdMap.BookingHistory} icon="fleet" className="details-tab" testId="bookingTabItem" >
                            &nbsp;&nbsp;Booking History
                        </Tabs.HeaderItem>
                        <Tabs.HeaderItem tabId={TabIdMap.TripHistory} icon="fleet" className="details-tab" testId="tripHistoryTabItem" disabled={isGuestMode}>
                            &nbsp;&nbsp;Trip History
                        </Tabs.HeaderItem>
                        <Tabs.HeaderItem tabId={TabIdMap.AuditTrail} icon="details" className="details-tab" testId="auditTrailTabItem" disabled={isGuestMode}>
                            &nbsp;&nbsp;Audit Trail
                        </Tabs.HeaderItem>
                        <Tabs.HeaderItem tabId={TabIdMap.Agreements} icon="details" className="details-tab" testId="agreementsTabItem" disabled={isGuestMode}>
                            &nbsp;&nbsp;Agreements
                        </Tabs.HeaderItem>
                        <Tabs.HeaderItem tabId={TabIdMap.Comments} icon="speech-bubble-question-mark" className="details-tab" testId="commentsTabItem" disabled={isGuestMode}>
                            &nbsp;&nbsp;Comments
                        </Tabs.HeaderItem>
                    </Tabs.Header >
                    <Tabs.Content>
                        <Tabs.ContentItem aria-labelledby={TabIdMap.PersonalData} tabId={TabIdMap.PersonalData}>
                            <div className="customer-detail">
                                <PersonalDataView />
                            </div>
                        </Tabs.ContentItem>
                        <Tabs.ContentItem aria-labelledby={TabIdMap.IdVerificationData} tabId={TabIdMap.IdVerificationData}>
                            <div className="customer-detail">
                                <IdVerificationDataView />
                            </div>
                        </Tabs.ContentItem>
                        <Tabs.ContentItem aria-labelledby={TabIdMap.PaymentData} tabId={TabIdMap.PaymentData}>
                            <div className="customer-detail">
                                <PaymentMethodDataView />
                            </div>
                        </Tabs.ContentItem>
                        <Tabs.ContentItem aria-labelledby={TabIdMap.BookingHistory}
                            tabId={TabIdMap.BookingHistory}>
                            <div className="customer-detail">
                                <BookingHistoryDataFilter />
                            </div>
                            <BookingHistoryDataView />
                        </Tabs.ContentItem>
                        <Tabs.ContentItem aria-labelledby={TabIdMap.TripHistory}
                            tabId={TabIdMap.TripHistory}>
                            <div className="customer-detail">
                                <TripHistoryDataFilter />
                            </div>
                            <div className="customer-detail">
                                <TripHistoryDataView />
                            </div>
                        </Tabs.ContentItem>
                        <Tabs.ContentItem aria-labelledby={TabIdMap.AuditTrail} tabId={TabIdMap.AuditTrail}>
                            <div className="customer-detail">
                                <AuditTrailDataFilter />
                            </div>
                            <div className="customer-detail">
                                <AuditTrailDataView />
                            </div>
                        </Tabs.ContentItem>
                        <Tabs.ContentItem aria-labelledby={TabIdMap.Agreements} tabId={TabIdMap.Agreements}>
                            <div className="customer-detail">
                                <AgreementsDataView />
                            </div>
                        </Tabs.ContentItem>
                        <Tabs.ContentItem aria-labelledby={TabIdMap.Comments} tabId={TabIdMap.Comments}>
                            <div className="customer-detail">
                                <CommentsDataView />
                            </div>
                        </Tabs.ContentItem>
                    </Tabs.Content >
                </Tabs >
            </div >
        </AppWrapper >
    );
}
